import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

/* ============================================================
   use-rebooking-predictions — Smart Rebooking Predictions
   Feature 4: Predicts when clients are due for rebooking
   based on their historical booking cadence.
   ============================================================ */

export interface RebookingPrediction {
  client_name: string
  client_email: string
  last_visit: string
  avg_interval_days: number
  predicted_rebook: string
  days_overdue: number
  is_at_risk: boolean
}

export interface RebookingSummary {
  total_predictions: number
  at_risk: number
  due_within_7d: number
  due_within_30d: number
}

interface RebookingResponse {
  predictions: RebookingPrediction[]
  summary: RebookingSummary
}

export function useRebookingPredictions(tenantId: string | undefined) {
  return useQuery<RebookingResponse>({
    queryKey: ['rebooking-predictions', tenantId],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rebooking-predictor`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ action: 'predict', tenant_id: tenantId }),
        }
      )

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }))
        throw new Error(err.error ?? `Rebooking prediction failed (${res.status})`)
      }

      return res.json()
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
